// src/services/notificationService.js
// All paths are relative to API_BASE_URL (e.g. https://host/api)
// Backend notifications/urls.py routes:
//   GET  notifications/                       → get_notifications
//   GET  notifications/unread-count/           → unread_count
//   POST notifications/<id>/read/             → mark_as_read
//   POST notifications/mark-all-read/         → mark_all_read
//   DELETE notifications/<id>/delete/         → delete_notification
//   DELETE notifications/delete-all/          → delete_all_notifications
//   POST notifications/register-token/        → register_push_token
//   POST notifications/unregister-token/      → unregister_push_token

import axiosInstance from '../utils/axiosConfig';
import logger from '../utils/logger';

class NotificationService {
  /**
   * Get all notifications for the current user
   * GET /api/notifications/
   * Returns: { notifications: [...], unread_count: N }
   */
  async getNotifications(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `notifications/${queryParams ? `?${queryParams}` : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      // 401 = not logged in — return empty silently, no need to spam the console
      if (error.response?.status === 401) return { notifications: [], unread_count: 0 };
      logger.error('Error fetching notifications:', error);
      throw this.handleError(error, 'Failed to fetch notifications');
    }
  }

  /**
   * Get unread notification count
   * GET /api/notifications/unread-count/
   * Returns: { unread_count: N }
   */
  async getUnreadCount() {
    try {
      const response = await axiosInstance.get('notifications/unread-count/');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) return { unread_count: 0 };
      logger.error('Error fetching unread count:', error);
      throw this.handleError(error, 'Failed to fetch unread count');
    }
  }

  /**
   * Mark a notification as read
   * POST /api/notifications/<id>/read/
   */
  async markAsRead(notificationId) {
    try {
      const response = await axiosInstance.post(`notifications/${notificationId}/read/`, {});
      return response.data;
    } catch (error) {
      logger.error('Error marking notification as read:', error);
      throw this.handleError(error, 'Failed to mark notification as read');
    }
  }

  /**
   * Mark all notifications as read
   * POST /api/notifications/mark-all-read/
   */
  async markAllAsRead() {
    try {
      const response = await axiosInstance.post('notifications/mark-all-read/', {});
      return response.data;
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
      throw this.handleError(error, 'Failed to mark all notifications as read');
    }
  }

  /**
   * Delete a notification
   * DELETE /api/notifications/<id>/delete/
   */
  async deleteNotification(notificationId) {
    try {
      const response = await axiosInstance.delete(`notifications/${notificationId}/delete/`);
      return response.data;
    } catch (error) {
      logger.error('Error deleting notification:', error);
      throw this.handleError(error, 'Failed to delete notification');
    }
  }

  /**
   * Delete all notifications
   * DELETE /api/notifications/delete-all/
   */
  async deleteAllNotifications() {
    try {
      const response = await axiosInstance.delete('notifications/delete-all/');
      return response.data;
    } catch (error) {
      logger.error('Error deleting all notifications:', error);
      throw this.handleError(error, 'Failed to delete all notifications');
    }
  }

  /**
   * Register push notification token
   * POST /api/notifications/register-token/
   */
  async registerPushToken(token, platform = 'web') {
    try {
      const response = await axiosInstance.post('notifications/register-token/', {
        token,
        device_type: platform,
      });
      return response.data;
    } catch (error) {
      logger.error('Error registering push token:', error);
      throw this.handleError(error, 'Failed to register push token');
    }
  }

  /**
   * Unregister push notification token
   * POST /api/notifications/unregister-token/
   */
  async unregisterPushToken(token) {
    try {
      const response = await axiosInstance.post('notifications/unregister-token/', { token });
      return response.data;
    } catch (error) {
      logger.error('Error unregistering push token:', error);
      throw this.handleError(error, 'Failed to unregister push token');
    }
  }

  /**
   * Handle API errors consistently
   */
  handleError(error, defaultMessage) {
    const message =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      error.message ||
      defaultMessage;

    logger.error('Notification Service Error:', {
      message,
      status: error.response?.status,
      data: error.response?.data,
    });

    return new Error(message);
  }

  /**
   * Format notification for display
   */
  formatNotification(notification) {
    return {
      ...notification,
      timeAgo: this.getTimeAgo(notification.created_at),
      typeInfo: this.getNotificationTypeInfo(notification.notification_type || notification.type),
    };
  }

  /**
   * Get human-readable time ago string
   */
  getTimeAgo(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    return date.toLocaleDateString();
  }

  /**
   * Get notification type information
   */
  getNotificationTypeInfo(type) {
    const typeMap = {
      order_created:         { icon: '🛍️', color: 'blue',   title: 'New Order',            priority: 'medium' },
      order_assigned:        { icon: '👤', color: 'green',  title: 'Order Assigned',       priority: 'medium' },
      order_started:         { icon: '▶️', color: 'indigo', title: 'Order Started',        priority: 'medium' },
      order_completed:       { icon: '✅', color: 'green',  title: 'Order Completed',      priority: 'high'   },
      order_cancelled:       { icon: '❌', color: 'red',    title: 'Order Cancelled',      priority: 'high'   },
      payment_received:      { icon: '💰', color: 'green',  title: 'Payment Received',     priority: 'high'   },
      payment_failed:        { icon: '💳', color: 'red',    title: 'Payment Failed',       priority: 'high'   },
      verification_approved: { icon: '✅', color: 'green',  title: 'Verification Approved',priority: 'high'   },
      verification_rejected: { icon: '❌', color: 'red',    title: 'Verification Rejected',priority: 'high'   },
      message:               { icon: '💬', color: 'blue',   title: 'New Message',          priority: 'medium' },
      review:                { icon: '⭐', color: 'yellow', title: 'New Review',           priority: 'low'    },
      system:                { icon: '⚙️', color: 'gray',   title: 'System Notification',  priority: 'low'    },
      info:                  { icon: '🔔', color: 'blue',   title: 'Notification',         priority: 'low'    },
    };
    return typeMap[type] || { icon: '🔔', color: 'gray', title: 'Notification', priority: 'low' };
  }

  /**
   * Show browser notification
   */
  showBrowserNotification(title, options = {}) {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        icon: '/logo192.png',
        badge: '/logo192.png',
        ...options,
      });
      if (options.onClick) notification.onclick = options.onClick;
      setTimeout(() => notification.close(), 5000);
      return notification;
    }
  }

  /**
   * Request notification permission
   */
  async requestNotificationPermission() {
    if (!('Notification' in window)) return false;
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      logger.error('Error requesting notification permission:', error);
      return false;
    }
  }
}

const notificationService = new NotificationService();

export const {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  deleteAllNotifications,
  registerPushToken,
  unregisterPushToken,
  formatNotification,
  getTimeAgo,
  getNotificationTypeInfo,
  showBrowserNotification,
  requestNotificationPermission,
} = notificationService;

export { notificationService };
export default notificationService;
